import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { GolfScoreCardModel } from 'src/app/_interfaces/golf/golf-score-card';
import { GolfPlayer } from 'src/app/_interfaces';
import { GameType, HoleResult } from 'src/app/_interfaces/golf/golf-tournament';
import { AppStringComponent } from '../app-string/app-string.component';

@Component({
    selector: 'app-matchplay-playoff-table',
    imports: [CommonModule, AppStringComponent],
    templateUrl: './matchplay-playoff-table.component.html',
    styleUrls: ['./matchplay-playoff-table.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MatchplayPlayoffTableComponent {
    @Input({ required: true }) left: GolfScoreCardModel[] = [];
    @Input({ required: true }) right: GolfScoreCardModel[] = [];
    @Input() extraAggregate: HoleResult[] | null = null;
    @Input() gameType: GameType | null = null;
    @Input() isOverview = false;

    private rf(p?: GolfScoreCardModel | null) {
        const r = p?.round_final ?? null;
        return {
            extra_pars: Array.isArray(r?.extra_course_pars) ? r.extra_course_pars as Array<number | null> : [],
            extra_scores: Array.isArray(r?.extra_scores) ? r.extra_scores as Array<number | null> : []
        };
    }

    get extraCount(): number {
        const l0 = this.left?.[0];
        const r0 = this.right?.[0];
        const rl = l0 ? this.rf(l0) : { extra_pars: [], extra_scores: [] };
        const rr = r0 ? this.rf(r0) : { extra_pars: [], extra_scores: [] };
        return Math.max(
            rl.extra_pars.length,
            rl.extra_scores.length,
            rr.extra_pars.length,
            rr.extra_scores.length
        );
    }

    extraParAt(i: number): number | string {
        const l = this.left[0] ? this.rf(this.left[0]).extra_pars[i] : null;
        const r = this.right[0] ? this.rf(this.right[0]).extra_pars[i] : null;
        return (l ?? r ?? '-') as number | string;
    }

    extraScoreAt(p: GolfScoreCardModel, i: number): string {
        const s = this.rf(p).extra_scores[i];
        return s == null ? '-' : String(s);
    }

    teamExtraScoreAt(side: 'left' | 'right', i: number): string {
        const arr = side === 'left' ? this.left : this.right;
        const card = arr.find(m => (this.rf(m).extra_scores.some(v => v != null))) ?? arr[0];
        if (!card) return '-';
        const s = this.rf(card).extra_scores[i];
        return s == null ? '-' : String(s);
    }

    private get isRyderCupMode(): boolean {
        for (const h of this.extraAggregate ?? []) {
            const lead = (h?.leader || '').toLowerCase();
            const win = (h?.hole_winner || '').toLowerCase();
            if (lead === 'europe' || lead === 'usa' || win === 'europe' || win === 'usa') return true;
            if (lead === 'team_1' || lead === 'team_2' || win === 'team_1' || win === 'team_2') return false;
        }
        const tL = (this.left?.[0]?.player?.ryder_cup_team || '').toLowerCase();
        const tR = (this.right?.[0]?.player?.ryder_cup_team || '').toLowerCase();
        return tL === 'europe' || tL === 'usa' || tR === 'europe' || tR === 'usa';
    }

    private sideTeam(side: 'left' | 'right'): 'eu' | 'us' | 'team1' | 'team2' {
        if (this.isRyderCupMode) {
            return ((side === 'left'
                ? (this.left?.[0]?.player?.ryder_cup_team || 'europe')
                : (this.right?.[0]?.player?.ryder_cup_team || 'europe')).toLowerCase() === 'usa') ? 'us' : 'eu';
        }
        return side === 'left' ? 'team1' : 'team2';
    }

    extraLeaderColor(i: number): 'eu' | 'us' | 'team1' | 'team2' | null {
        const v = (this.extraAggregate?.[i]?.leader || '').toLowerCase();
        if (this.isRyderCupMode) return v === 'usa' ? 'us' : v === 'europe' ? 'eu' : null;
        if (v === 'team_1') return 'team1';
        if (v === 'team_2') return 'team2';
        return null;
    }

    extraHoleWinner(i: number): 'eu' | 'us' | 'team1' | 'team2' | null {
        const v = (this.extraAggregate?.[i]?.hole_winner || '').toLowerCase();
        if (this.isRyderCupMode) return v === 'usa' ? 'us' : v === 'europe' ? 'eu' : null;
        if (v === 'team_1') return 'team1';
        if (v === 'team_2') return 'team2';
        return null;
    }

    extraWinnerClass(side: 'left' | 'right', i: number): string {
        const w = this.extraHoleWinner(i);
        if (!w) return '';
        const sideWon = (side === 'left' && this.sideTeam('left') === w) || (side === 'right' && this.sideTeam('right') === w);
        if (!sideWon) return '';
        return w === 'eu' ? 'win-eu' : w === 'us' ? 'win-us' : w === 'team1' ? 'win-team1' : 'win-team2';
    }

    overlayClass(scoreTxt: string, parVal: number | string): string | null {
        if (scoreTxt === '-' || parVal === '-') return null;
        const diff = Number(scoreTxt) - Number(parVal);
        if (diff === -1) return 'single-circle';
        if (diff <= -2) return 'double-circle';
        if (diff === 1) return 'single-box';
        if (diff >= 2) return 'double-box';
        return null;
    }

    getExtraCellClasses(_: 'left' | 'right', p: GolfScoreCardModel, i: number): string[] {
        const overlay = this.overlayClass(this.extraScoreAt(p, i), this.extraParAt(i));
        return [overlay || ''].filter(Boolean) as string[];
    }

    getTeamExtraCellClasses(side: 'left' | 'right', i: number): string[] {
        const overlay = this.overlayClass(this.teamExtraScoreAt(side, i), this.extraParAt(i));
        return [overlay || ''].filter(Boolean) as string[];
    }

    teamPlayers(side: 'left' | 'right'): GolfPlayer[] {
        const arr = side === 'left' ? this.left : this.right;
        return arr.map(x => x.player).filter(Boolean) as GolfPlayer[];
    }

    shortName(p: GolfPlayer | undefined): string {
        if (!p) return '';
        const f = (p.first_name || '').trim();
        const l = (p.last_name || '').trim();
        return `${f ? f[0] + '.' : ''} ${l}`.trim().toUpperCase();
    }

    extraScoreLabel(i: number): string {
        const a = this.extraAggregate?.[i];
        return (a?.score ?? '-')!.toString().toUpperCase();
    }

    get isFoursomes(): boolean {
        return (this.gameType || '').toLowerCase() === 'four_some';
    }

    mergeClasses(...items: Array<string | string[] | null | undefined>): string[] {
        const out: string[] = [];
        for (const it of items) {
            if (Array.isArray(it)) out.push(...(it.filter(Boolean) as string[]));
            else if (it) out.push(it);
        }
        return out;
    }

    get extraColumns(): number[] {
        return Array.from({ length: this.extraCount }, (_, i) => i);
    }
}
